import { Alert, Button, Form, Input, Modal, Switch, Typography, message } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useClearSapSettings, useSaveSapSettings } from './Hooks';
import { getErrorMessage } from '../../utils/errorHandler';

const { Text, Paragraph, Title } = Typography;

const isHttpUrl = (value) => /^https?:\/\/.+/i.test(String(value || '').trim());

const SapSettingsTab = ({ sapMeta = {}, canEdit = false }) => {
  const { t } = useTranslation(['settings', 'common']);
  const [form] = Form.useForm();
  const configured = Boolean(sapMeta?.configured);
  // Env may report passwordSet; only a CMS-stored password can be kept on save.
  const passwordSet = Boolean(sapMeta?.configured && sapMeta?.passwordSet);
  const enabledWatch = Form.useWatch('enabled', form);

  useEffect(() => {
    form.setFieldsValue({
      enabled: Boolean(sapMeta?.enabled),
      url: sapMeta?.url || '',
      username: sapMeta?.username || '',
      password: '',
    });
  }, [sapMeta, form]);

  const saveMutation = useSaveSapSettings({
    onSuccess: () => {
      form.setFieldValue('password', '');
      message.success(t('sapSaveSuccess'));
    },
    onError: (error) => message.error(getErrorMessage(error, t('common:fetchError'))),
  });

  const clearMutation = useClearSapSettings({
    onSuccess: () => {
      form.setFieldValue('password', '');
      message.success(t('sapClearSuccess'));
    },
    onError: (error) => message.error(getErrorMessage(error, t('common:fetchError'))),
  });

  const handleSave = (values) => {
    saveMutation.mutate({
      enabled: Boolean(values.enabled),
      url: (values.url || '').trim(),
      username: (values.username || '').trim(),
      password: (values.password || '').trim(),
    });
  };

  const confirmClear = () => {
    Modal.confirm({
      title: t('sapClearConfirmTitle'),
      content: t('sapClearConfirmBody'),
      okText: t('sapClearButton'),
      okType: 'danger',
      onOk: () => clearMutation.mutateAsync(),
    });
  };

  return (
    <div className="mt-4 max-h-[74vh] overflow-y-auto pr-4 pb-8">
      <div className="flex flex-col gap-5 max-w-2xl">
        <div>
          <Title level={5} className="!mb-1">
            {t('sapTabTitle')}
          </Title>
          <Text type="secondary">{t('sapTabDescription')}</Text>
        </div>

        <Alert
          type={configured ? 'success' : 'info'}
          showIcon
          className="!m-0"
          message={configured ? t('sapDbActiveHint') : t('sapEnvFallbackHint')}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          disabled={!canEdit}
        >
          <Form.Item
            name="enabled"
            label={t('sapEnabledLabel')}
            valuePropName="checked"
            extra={t('sapEnabledHelp')}
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="url"
            label={t('sapUrlLabel')}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const trimmed = String(value || '').trim();
                  if (!getFieldValue('enabled') && !trimmed) return Promise.resolve();
                  if (!trimmed) return Promise.reject(new Error(t('sapUrlRequired')));
                  if (!isHttpUrl(trimmed)) return Promise.reject(new Error(t('sapUrlInvalid')));
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input
              autoComplete="off"
              placeholder={t('sapUrlPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="username"
            label={t('sapUsernameLabel')}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!getFieldValue('enabled')) return Promise.resolve();
                  if (String(value || '').trim()) return Promise.resolve();
                  return Promise.reject(new Error(t('sapUsernameRequired')));
                },
              }),
            ]}
          >
            <Input autoComplete="off" placeholder={t('sapUsernamePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="password"
            label={t('sapPasswordLabel')}
            extra={passwordSet ? t('sapPasswordKeepHint') : undefined}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!getFieldValue('enabled')) return Promise.resolve();
                  if (passwordSet || String(value || '').trim()) return Promise.resolve();
                  return Promise.reject(new Error(t('sapPasswordRequired')));
                },
              }),
            ]}
          >
            <Input.Password
              autoComplete="new-password"
              placeholder={
                passwordSet ? t('sapPasswordPlaceholder') : t('sapPasswordRequiredPlaceholder')
              }
            />
          </Form.Item>

          {canEdit && (
            <Form.Item>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saveMutation.isPending}
                >
                  {t('saveButton')}
                </Button>
                <Button
                  danger
                  onClick={confirmClear}
                  loading={clearMutation.isPending}
                  disabled={!configured}
                >
                  {t('sapClearButton')}
                </Button>
              </div>
            </Form.Item>
          )}
        </Form>

        {enabledWatch && !passwordSet && !configured ? (
          <Alert type="warning" showIcon className="!m-0" message={t('sapEnableNeedsPassword')} />
        ) : null}

        <Paragraph type="secondary" className="!mb-0">
          {t('sapSecurityNote')}
        </Paragraph>
      </div>
    </div>
  );
};

export default SapSettingsTab;
